import { call } from '@advanza/api'
import { Checkbox, FileDropZone } from '@advanza/input'
import { Button, Divider } from '@advanza/ui'
import { useState } from 'react'
import LinearLoader from '../LinearLoader'

interface WizardImportActionProps {
    serviceId: number
}

const COLUMN_DESCRIPTIONS: Array<[string, string]> = [
    [
        'Question: Order',
        'Question order (integer). Rows sharing the same Order belong to the same question.',
    ],
    ['Question: Text', 'The question text shown to end users.'],
    [
        'Question: Type',
        'Input type: checkbox, radio, text, textarea, number, exec_date, day_picker, file, location, month_picker, moment_picker.',
    ],
    [
        'Option: Answer',
        'One answer option. Leave blank for free-text types (text, textarea, number, ...).',
    ],
    ['Question: Status*', 'new, active, or hidden.'],
    ['Question: Optional*', 'Whether the end user can skip this question (yes/no).'],
    ['Question: Filterable*', 'Whether answers appear in filter UIs (yes/no).'],
    [
        'Question: Privacy sensitive*',
        'Whether answers should be treated as privacy sensitive (yes/no).',
    ],
    ['Question: Short*', 'Short label used in filters.'],
    ['Question: PRO*', 'Variant of the question shown to the professional.'],
    ['Question: Description*', 'Helper text shown below the question.'],
    ['Question: Emoji prefix*', 'Emoji shown for this question (for example: 😀).'],
    ['Question: Placeholder*', 'Ghost text inside the input.'],
    ['Question: After conversion*', 'Whether to ask this question after conversion (yes/no).'],
    [
        'Question: Show when*',
        "Show this question only when an earlier answer in this import is selected. Separate multiple values with '|'. Use 'Order:value' to disambiguate when the same answer exists on multiple earlier questions. The referenced question must be included in the import file.",
    ],
    ['Option: Status*', 'new, active, or hidden.'],
    ['Option: Default wizard answer*', 'Pre-select this answer (yes/no).'],
    [
        'Option: Default keyword filter*',
        'Pre-filter by keyword when this answer is selected (yes/no).',
    ],
    ['Option: Order*', 'Sort order for this answer (integer).'],
]

const WizardImportAction = ({ serviceId }: WizardImportActionProps) => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(false)
    const [result, setResult] = useState('')
    const [dryRun, setDryRun] = useState(true)
    const [isColumnDescriptionsOpen, setIsColumnDescriptionsOpen] = useState(false)

    const handleUpload = (files: File[]) => {
        setLoading(true)
        setResult('')
        setError(false)

        call('office/services/import-wizard/' + serviceId, {
            payload: {
                file: files[0],
                options: JSON.stringify({ dry_run: dryRun }),
            },
        }).then(
            (response) => {
                setResult(response.message || JSON.stringify(response))
                setError(false)
                setLoading(false)
            },
            (response) => {
                setResult(response.error || JSON.stringify(response))
                setError(true)
                setLoading(false)
            },
        )
    }

    return (
        <div>
            <div>
                Upload a CSV or XLSX file matching the template. Required columns: Question: Order,
                Question: Text, Question: Type, Option: Answer. Other columns are optional. Yes/no
                columns accept yes/no, true/false, or 1/0.
            </div>
            <details
                style={{ marginTop: 12 }}
                onToggle={(event) =>
                    setIsColumnDescriptionsOpen((event.currentTarget as HTMLDetailsElement).open)
                }>
                <summary>
                    <span style={{ marginRight: 6 }}>{isColumnDescriptionsOpen ? '▾' : '▸'}</span>
                    Column descriptions ( * optional )
                </summary>
                <div style={{ marginTop: 8 }}>
                    {COLUMN_DESCRIPTIONS.map(([label, desc]) => (
                        <div key={label} style={{ marginBottom: 8 }}>
                            <strong>{label}</strong>: {desc}
                        </div>
                    ))}
                </div>
            </details>
            <Divider m />
            <Checkbox
                value={dryRun}
                onChange={() => setDryRun(!dryRun)}
                title="Dry run (preview only, no changes saved)"
            />
            <Divider m />
            <FileDropZone onFilesAccepted={handleUpload}>
                <Button>{dryRun ? 'Preview import' : 'Import wizard'}</Button>
            </FileDropZone>
            <Divider m />
            <a target="_blank" rel="noreferrer" href="/api/office/services/import-wizard-template/">
                <Button name="borderedPoppins">Download template</Button>
            </a>
            <Divider m />
            {result && (
                <div style={{ color: error ? 'red' : 'green', whiteSpace: 'pre-wrap' }}>
                    {result}
                </div>
            )}
            {loading && <LinearLoader absolute />}
        </div>
    )
}

export default WizardImportAction
